import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, every, first, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { LgvAPIService, LgvConstants, LgvUserService, NotificationService } from '../../core/core.module';
import { BusinessType } from '../../core/enums/shared';
import { LgvCompanyService } from '../../core/services/lgv-company.service';
import { ContentLocation, DisclosureType } from '../../core/utility/lgv-constants';
import { GreaterThanZero } from '../../core/utility/lgv-form-validator';
import { LgvModel } from '../../data-models/longevity.model';
import { StdObj } from '../../data-models/standard-objects';

@Component({
  selector: 'lus-disclaimers-modal',
  templateUrl: './disclaimers-modal.component.html',
  styleUrls: ['./disclaimers-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DisclaimersModalComponent implements OnInit, OnDestroy {

    isLoading$ = new BehaviorSubject(false);
    onDestroy$ = new Subject();

    disclaimerFG: UntypedFormGroup;

    companyInfoParam: LgvModel.CompanyInfo = null;

    disclaimerList$ = new BehaviorSubject([]);
    get disclaimerArray() { return this.disclaimerFG.controls["disclaimerArray"] as UntypedFormArray; }

    // Make visible for the UI
    readonly DisclosureType = DisclosureType;


    constructor(private fb: UntypedFormBuilder,
        public lgvUserSvc: LgvUserService,
        private route: ActivatedRoute,
        public lgvApiSvc: LgvAPIService,
        public lgvConstants: LgvConstants,
        private lgvCompanySvc: LgvCompanyService,
        private notificationSvc: NotificationService,
        public dialogRef: MatDialogRef<DisclaimersModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any) {

        if (this.data?.companyInfo) {
            this.companyInfoParam = this.data?.companyInfo;
        }

        if (this.data?.branding$ && this.data.branding$.value) {
            //this.branding$.next(this.data.branding$.value);

            let disclosuresTemp:LgvModel.Disclosure[] = this.data.branding$.value?.disclosures;
            this.disclaimerList$.next(disclosuresTemp.sort((x, y) => x.displayOrder - y.displayOrder));
        }
    }

    ngOnInit(): void {
        this.initializeForm();
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    initializeForm() {

        this.disclaimerFG = this.fb.group({
            disclaimerArray: this.fb.array([])
        });


        for (let d of this.disclaimerList$.value) {
            let disc: LgvModel.Disclosure = d;
            this.onAddDisclaimer(disc.typeId, disc.type, disc.title, disc.value, disc.contentLocations, disc.displayOrder, disc.isForCobrandedClientOnly);
        }
    }


    onAddFooterLink() {

        let location: LgvModel.ContentLocation = { id: ContentLocation.Footer, name: 'Footer' };

        const fg = this.fb.group({
            typeId: [DisclosureType.Link, [Validators.required]],
            type: ['Link', [Validators.required]],
            title: [null, [Validators.required]],
            value: [null, [Validators.required]],
            displayOrder: [1, [Validators.required]],
            contentLocations: [[location], []],
            isForCobrandedClientOnly: [null]
        });

        this.disclaimerArray.push(fg);
    }

    onAddEmailDisclaimer() {

        let location: LgvModel.ContentLocation = { id: ContentLocation.Email, name: 'Email' };

        const fg = this.fb.group({
            typeId: [DisclosureType.Text, [Validators.required]],
            type: ['Text', [Validators.required]],
            title: [null, []],
            value: [null, [Validators.required]],
            displayOrder: [1, [Validators.required]],
            contentLocations: [[location], []],
            isForCobrandedClientOnly: [null]
        });

        this.disclaimerArray.push(fg);
    }

    onAddDisclaimer(typeId: string, type: string, title: string, value: string, locations: LgvModel.ContentLocation[], displayOrder: number, isForCobrandedClientOnly: boolean) {
        let isText = typeId.toUpperCase() === DisclosureType.Text;

        const fg = this.fb.group({
            typeId: [typeId, [Validators.required]],
            type: [type, [Validators.required]],
            title: [title, isText ? [] : [Validators.required]],
            value: [value, [Validators.required]],
            displayOrder: [displayOrder, [Validators.required]],
            contentLocations: [locations, [Validators.required]],
            isForCobrandedClientOnly: [isForCobrandedClientOnly]
        });

        this.disclaimerArray.push(fg);
    }

    removeDisclaimer(index: number) {
        this.disclaimerArray.removeAt(index);
    }

    onSubmit() {
        this.isLoading$.next(true);

        let model: any = {
            disclosures: this.disclaimerArray.getRawValue()
        };
        
        this.lgvCompanySvc.saveBranding(this.companyInfoParam.id, model).pipe(first(), takeUntil(this.onDestroy$)).subscribe(x => {
            this.isLoading$.next(false);
            setTimeout(() => {
                this.notificationSvc.success('Disclosures have been saved');
            }, 500)
            this.dialogRef.close({ action: true });
        },
            (err: Error) => {
                this.isLoading$.next(false);
                const msg = 'There was an error saving the disclaimers';
                console.log(msg);
                this.notificationSvc.error(msg);
            }
        );
    }

    onCancel() {
        this.dialogRef.close({ action: false });
    }

    testObj(obj: any) {
        console.log('obj:');
        console.log(obj);
    }

    onDrop(event: CdkDragDrop<string[]>, discType: string) {
        // Both links and text are considered Disclosures.  We only want to sort items of "discType".  Don't want to mess w/ oranges when sorting apples.

        let index = 0;
        //console.log('onDrop() - DisclaimerType: ' + discType);
        //console.log(event);

        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

        for (let ctrl of this.disclaimerArray.controls) {
            let model = ctrl.getRawValue();
            if (model?.typeId?.toUpperCase() !== discType) {
                //console.log('Skipping this item:');
                //console.log(model);
                continue;
            }
            model.displayOrder = ++index;
            ctrl.setValue(model);
        }
    }
}
